import { useEffect, useState } from 'react';
import { isReleased, type GameId, type SkillGameId } from '@tg/shared';
import { useSession } from '@/stores/session';
import { skillApi } from './api';
import { localSaves } from './localSave';
import { useLastGame } from './recent';

/** What «Continue» at the top of the lobby offers. */
export type ContinueItem =
  /** an unfinished skill session: its game page resumes it (local save or server session) */
  | { kind: 'session'; game: SkillGameId; source: 'local' | 'server'; id: string; updatedAt: number }
  /** the last game opened on this device */
  | { kind: 'game'; game: GameId; at: number };

/** The newest unfinished skill session (released games only), local saves first checked, then the server's. */
export function useUnfinishedSession(): Extract<ContinueItem, { kind: 'session' }> | null {
  const signedIn = useSession((s) => s.status === 'user');
  const [server, setServer] = useState<Extract<ContinueItem, { kind: 'session' }> | null>(null);
  useEffect(() => {
    if (!signedIn) {
      setServer(null);
      return;
    }
    let live = true;
    skillApi
      .list({ status: 'active', limit: 5 })
      .then((r) => {
        const s = r.sessions.find((x) => isReleased(x.game));
        if (live) setServer(s ? { kind: 'session', game: s.game, source: 'server', id: s.id, updatedAt: Date.parse(s.updatedAt) } : null);
      })
      .catch(() => live && setServer(null));
    return () => {
      live = false;
    };
  }, [signedIn]);
  const local = localSaves
    .list()
    .filter((s) => !s.completed && isReleased(s.game))
    .map((s) => ({ kind: 'session' as const, game: s.game, source: 'local' as const, id: s.clientId, updatedAt: Date.parse(s.updatedAt) }))
    .sort((a, b) => b.updatedAt - a.updatedAt)[0];
  if (local && (!server || local.updatedAt >= server.updatedAt)) return local;
  return server;
}

/** «Continue»: an unfinished session, else the last game opened; null — nothing (a first visit). */
export function useContinue(): ContinueItem | null {
  const session = useUnfinishedSession();
  const last = useLastGame();
  if (session) return session;
  return last ? { kind: 'game', game: last.game, at: last.at } : null;
}
